import { t } from '@lingui/core/macro';
import { BrandInsightsGranularity, BrandInsightsPeriod, type MoneyRecord } from '@navio/nsdk';
import {
  CustomDateFormats,
  formatCompactCurrencyAmount,
  formatCurrencyAmount,
  formatDecimal,
  noddiFormatDate
} from 'noddi-util';

export const formatCount = (value: number) => formatDecimal(value, 0);

export const formatMoney = (money: MoneyRecord) => formatCurrencyAmount({ money, decimals: 0 });

export const formatCompactMoney = (amount: number, currency: MoneyRecord['currency']) =>
  formatCompactCurrencyAmount({ money: { amount, currency } });

export const formatShare = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)}%` : '0%');

/** Relative change as a fraction (0.12 = +12%); null when there is no baseline to compare with. */
export const getRelativeChange = (current: number, previous: number) => (previous > 0 ? current / previous - 1 : null);

/** Signed percentage, e.g. "+12.4%". */
export const formatChange = (change: number) => `${change >= 0 ? '+' : '−'}${Math.abs(change * 100).toFixed(1)}%`;

export const getPeriodOptions = () => [
  { value: BrandInsightsPeriod.LAST_30_DAYS, label: t`Last 30 days` },
  { value: BrandInsightsPeriod.SEASON, label: t`This season` },
  { value: BrandInsightsPeriod.YEAR_TO_DATE, label: t`Year to date` },
  { value: BrandInsightsPeriod.ALL_TIME, label: t`All time` }
];

// Every period with a comparison compares with the same dates one year earlier: bookings are seasonal,
// so the span just before says little.
export const getComparisonLabel = () => t`vs same period last year`;

/** Names the comparison series in the chart legend and tooltip. */
export const getComparisonSeriesLabel = () => t`Same period last year`;

export const formatBucketLabel = (bucketStart: string, granularity: BrandInsightsGranularity) => {
  if (granularity === BrandInsightsGranularity.MONTH) {
    return noddiFormatDate(bucketStart, CustomDateFormats.FULL_MONTH_AND_YEAR);
  }
  return noddiFormatDate(bucketStart, CustomDateFormats.SHORT_DAY_SHORT_MONTH);
};

export const formatDateRange = (fromDate: string, toDate: string) =>
  `${noddiFormatDate(fromDate, CustomDateFormats.SHORT_DAY_SHORT_MONTH_YEAR)} – ${noddiFormatDate(
    toDate,
    CustomDateFormats.SHORT_DAY_SHORT_MONTH_YEAR
  )}`;
